import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';
import cx from 'classnames';

import { EditorContext } from '../../index';
import { updateWidgetProperty } from 'actions/editor';
import { Property } from '../../components/properties';
import Tooltip from 'components/Tooltip';

import { DEFAULT_FLEX_LAYOUT, resolveFlexLayout, resolvePadding, paddingToStored, paddingToCss, flexValueToCss, pxToNumber, numberToPx } from './flexContext';
import { FLEX_ICONS } from './flexIcons';

import styles from './flexConfig.module.scss';

const FLEX_CONFIG_PORTAL_SELECTORS = [
  '[class*="dropdown__menu"]',
  '[class*="menu-portal"]',
];

function isInsideFlexConfigPanel(target, dialogClassName) {
  if (!(target instanceof Element)) {
    return false;
  }
  if (dialogClassName && target.closest(`.${dialogClassName}`)) {
    return true;
  }
  return FLEX_CONFIG_PORTAL_SELECTORS.some((selector) => target.closest(selector));
}

export function useFlexConfigDismissOnClickOutside(onCancel, dialogClassName) {
  const onCancelRef = React.useRef(onCancel);
  onCancelRef.current = onCancel;

  React.useEffect(() => {
    if (!onCancelRef.current || !dialogClassName) {
      return;
    }

    const handleMouseDown = (e) => {
      if (isInsideFlexConfigPanel(e.target, dialogClassName)) {
        return;
      }
      onCancelRef.current?.(e);
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [dialogClassName]);
}

export const FLEX_OPTIONS = {
  flexDirection: [
    { value: 'row', label: 'Row' },
    { value: 'column', label: 'Column' },
    { value: 'rowReverse', label: 'Row ↺' },
    { value: 'columnReverse', label: 'Col ↺' },
  ],
  justifyContent: [
    { value: 'flexStart', label: 'Start' },
    { value: 'center', label: 'Center' },
    { value: 'flexEnd', label: 'End' },
    { value: 'spaceBetween', label: 'Between' },
    { value: 'spaceAround', label: 'Around' },
    { value: 'spaceEvenly', label: 'Evenly' },
  ],
  alignItems: [
    { value: 'stretch', label: 'Stretch' },
    { value: 'flexStart', label: 'Start' },
    { value: 'center', label: 'Center' },
    { value: 'flexEnd', label: 'End' },
    { value: 'baseline', label: 'Baseline' },
  ],
  alignContent: [
    { value: 'flexStart', label: 'Start' },
    { value: 'center', label: 'Center' },
    { value: 'flexEnd', label: 'End' },
    { value: 'spaceBetween', label: 'Between' },
    { value: 'spaceAround', label: 'Around' },
    { value: 'stretch', label: 'Stretch' },
  ],
  flexWrap: [
    { value: 'nowrap', label: 'No wrap' },
    { value: 'wrap', label: 'Wrap' },
    { value: 'wrapReverse', label: 'Wrap ↺' },
  ],
};

export const FLEX_TOOLTIPS = {
  flexDirection: {
    row: 'Left to right',
    column: 'Top to bottom',
    rowReverse: 'Right to left',
    columnReverse: 'Bottom to top',
  },
  justifyContent: {
    flexStart: 'Align start',
    center: 'Center',
    flexEnd: 'Align end',
    spaceBetween: 'Space between',
    spaceAround: 'Space around',
    spaceEvenly: 'Space evenly',
  },
  alignItems: {
    stretch: 'Stretch to fit',
    flexStart: 'Align top',
    center: 'Center',
    flexEnd: 'Align bottom',
    baseline: 'Align to text',
  },
  alignContent: {
    flexStart: 'Rows at top',
    center: 'Rows centered',
    flexEnd: 'Rows at bottom',
    spaceBetween: 'Space between rows',
    spaceAround: 'Space around rows',
    stretch: 'Stretch rows to fit',
  },
  flexWrap: {
    nowrap: 'Single line',
    wrap: 'Wrap to new lines',
    wrapReverse: 'Wrap in reverse',
  },
};

const FLEX_OPTION_VALUES = _.mapValues(FLEX_OPTIONS, options => new Set(_.map(options, 'value')));

function sanitizeFlexLayout(flexLayout, defaults) {
  const result = { ...flexLayout };
  _.forEach(FLEX_OPTION_VALUES, (validValues, key) => {
    if (!validValues.has(result[key])) {
      result[key] = defaults[key];
    }
  });
  return result;
}

const AXIS_AWARE_FIELDS = new Set(['justifyContent', 'alignItems', 'alignContent', 'flexWrap']);

export const FlexOptionRow = ({ name, field, options, value, onChange, direction, disabled }) => {
  const icons = FLEX_ICONS[field] || {};
  const tooltips = FLEX_TOOLTIPS[field] || {};
  const isColumn = direction === 'column' || direction === 'columnReverse';
  const rotate = isColumn && AXIS_AWARE_FIELDS.has(field);
  return (
    <Property name={name} className={styles.flexOptionRow}>
      <div className={cx(styles.options, { [styles.disabled]: disabled })}>
        {options.map(option => {
          const Icon = icons[option.value];
          const tooltip = tooltips[option.value] || option.label;
          return (
            <Tooltip key={option.value} placement="top" content={tooltip}>
              <button
                disabled={disabled}
                className={cx(styles.alignment, {
                  [styles.active]: value === option.value,
                  [styles.iconButton]: !!Icon,
                  [styles.rotate]: rotate && !!Icon,
                })}
                onClick={() => !disabled && onChange(option.value)}
                aria-label={tooltip}
              >
                {Icon ? <Icon size={24} /> : option.label}
              </button>
            </Tooltip>
          );
        })}
      </div>
    </Property>
  );
};

const PADDING_SIDES = [
  { key: 'topLeft', label: 'TOP' },
  { key: 'topRight', label: 'RIGHT' },
  { key: 'bottomRight', label: 'BOTTOM' },
  { key: 'bottomLeft', label: 'LEFT' },
];

export const FlexPaddingRow = ({ name, value, onChange }) => {
  const padding = resolvePadding(value);
  const { lock } = padding;

  const emit = (sidePadding) => onChange(paddingToStored(sidePadding));

  const handleToggleLock = () => {
    const nextLock = !lock;
    if (nextLock) {
      emit({ topLeft: padding.topLeft, topRight: padding.topLeft, bottomRight: padding.topLeft, bottomLeft: padding.topLeft, lock: true });
      return;
    }
    emit({ ...padding, lock: false });
  };

  const handleChange = (side, e) => {
    let input = parseInt(e.target.value, 10) || 0;
    input = Math.max(0, Math.min(999, input));

    if (lock) {
      emit({ topLeft: input, topRight: input, bottomRight: input, bottomLeft: input, lock: true });
      return;
    }
    emit({ ...padding, [side]: input });
  };

  return (
    <Property name={name}>
      <div className={styles.padding}>
        <button
          className={cx(styles.lock, { [styles.active]: lock })}
          onClick={handleToggleLock}
          title={lock ? 'Padding: same on all sides' : 'Padding: per side'}
        >
          <span className="cleverstory-corner-radius">
            <span className="path1"></span>
            <span className="path2"></span>
            <span className="path3"></span>
            <span className="path4"></span>
            <span className="path5"></span>
            <span className="path6"></span>
          </span>
        </button>
        {PADDING_SIDES.map(({ key, label }) => (
          <div key={key} className={styles.side}>
            <input
              className={styles.value}
              type="text"
              pattern="[0-9]*"
              value={padding[key]}
              onChange={handleChange.bind(this, key)}
              onBlur={handleChange.bind(this, key)}
              placeholder="0"
            />
            <label>{label}</label>
          </div>
        ))}
      </div>
    </Property>
  );
};

export function flexContainerStyles(flexLayout, { baseDefaults = DEFAULT_FLEX_LAYOUT, minHeight = 200 } = {}) {
  const layout = _.defaults({}, flexLayout, baseDefaults);
  return {
    height: 'auto',
    minHeight: `${minHeight}px`,
    display: 'flex',
    flexDirection: flexValueToCss(layout.flexDirection),
    justifyContent: flexValueToCss(layout.justifyContent),
    alignItems: flexValueToCss(layout.alignItems),
    alignContent: flexValueToCss(layout.alignContent),
    flexWrap: flexValueToCss(layout.flexWrap),
    columnGap: numberToPx(layout.gap),
    rowGap: numberToPx(layout.gap),
    padding: paddingToCss(layout.padding),
    boxSizing: 'border-box',
  };
}

export const FlexConfigControls = ({ id, widget, baseDefaults, resolveLayout, disabled = false }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const effectiveDefaults = baseDefaults || DEFAULT_FLEX_LAYOUT;
  const resolve = resolveLayout || resolveFlexLayout;
  const flexLayout = sanitizeFlexLayout(
    resolve(widget, breakpoint, effectiveDefaults),
    effectiveDefaults
  );
  const ownLayout = _.get(widget, `properties.${breakpoint}.flexProperty`, {});

  const handleChange = (key, value) => {
    if (disabled) return;
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, {
      flexProperty: { ...ownLayout, [key]: value }
    }));
  }

  return (
    <>
      <FlexOptionRow name="Direction" field="flexDirection" options={FLEX_OPTIONS.flexDirection}
        value={flexLayout.flexDirection} disabled={disabled} onChange={(v) => handleChange('flexDirection', v)} />
      <Property.Divider />
      <FlexOptionRow name="Justify content" field="justifyContent" options={FLEX_OPTIONS.justifyContent}
        value={flexLayout.justifyContent} direction={flexLayout.flexDirection} disabled={disabled} onChange={(v) => handleChange('justifyContent', v)} />
      <Property.Divider />
      <FlexOptionRow name="Align items" field="alignItems" options={FLEX_OPTIONS.alignItems}
        value={flexLayout.alignItems} direction={flexLayout.flexDirection} disabled={disabled} onChange={(v) => handleChange('alignItems', v)} />
      <Property.Divider />
      <FlexOptionRow name="Wrap" field="flexWrap" options={FLEX_OPTIONS.flexWrap}
        value={flexLayout.flexWrap} direction={flexLayout.flexDirection} disabled={disabled} onChange={(v) => handleChange('flexWrap', v)} />
      {flexLayout.flexWrap !== 'nowrap' &&
        <>
          <Property.Divider />
          <FlexOptionRow name="Align content" field="alignContent" options={FLEX_OPTIONS.alignContent}
            value={flexLayout.alignContent} direction={flexLayout.flexDirection} disabled={disabled} onChange={(v) => handleChange('alignContent', v)} />
        </>
      }
      <Property.Divider />
      <Property name="Gap">
        <div className={cx({ [styles.disabled]: disabled })}>
          <Property.Slider
            min={0} max={100} input
            value={pxToNumber(flexLayout.gap)}
            onChange={(value) => handleChange('gap', numberToPx(value))}
          />
        </div>
      </Property>
      <Property.Divider />
      <div className={cx({ [styles.disabled]: disabled })}>
        <FlexPaddingRow name="Padding"
          value={flexLayout.padding}
          onChange={(value) => handleChange('padding', value)} />
      </div>
    </>
  );
}

export const LayoutConfigPopup = ({
  id,
  widget,
  baseDefaults,
  flexMode,
  onToggle,
  title = 'Layout',
  renderFlexControls,
  target,
  targetRef,
  ...rest
}) => {
  useFlexConfigDismissOnClickOutside(rest.onCancel, styles.flexConfigDialog);

  const [confirming, setConfirming] = React.useState(false);

  const layoutDialogRef = React.useRef(null);
  const fallbackTarget = `#${id}`;

  const handleSelectFluid = () => {
    setConfirming(false);
    if (flexMode) {
      onToggle(false);
    }
  };

  const handleSelectFlex = () => {
    if (flexMode) return;
    setConfirming(true);
  };

  const handleConfirmFlex = () => {
    setConfirming(false);
    onToggle(true);
  };

  return (
    <>
      <Property.Dialog className={styles.flexConfigDialog}
        ref={layoutDialogRef}
        width={280}
        targetRef={targetRef}
        target={target || fallbackTarget}
        placement="bottom"
        title={title}
        onCancel={rest.onCancel}
      >
        <Property.Body>
          <Property name="Layout">
            <div className={styles.layoutToggle}>
              <button
                className={cx(styles.layoutOption, { [styles.active]: !flexMode })}
                onClick={handleSelectFluid}
              >
                Fluid
              </button>
              <button
                className={cx(styles.layoutOption, { [styles.active]: flexMode })}
                onClick={handleSelectFlex}
              >
                Flex
              </button>
            </div>
          </Property>

          {flexMode &&
            <>
              <Property.Divider />
              {renderFlexControls
                ? renderFlexControls()
                : <FlexConfigControls id={id} widget={widget} baseDefaults={baseDefaults} />}
            </>
          }
        </Property.Body>
      </Property.Dialog>

      {confirming &&
        <Property.Dialog className={styles.flexConfigDialog}
          width={280}
          targetRef={targetRef}
          target={target || fallbackTarget}
          placement="bottom"
          title={'Flex Layout'}
          onCancel={() => setConfirming(false)}
        >
          <Property.Body>
            <div className={styles.flexConfirm}>
              <p className={styles.flexConfirmText}>
                Enabling Flex Mode causes all child elements to inherit the parent container's flex behavior.
              </p>
              <div className={styles.flexConfirmActions}>
                <button className={styles.cancel} onClick={() => setConfirming(false)}>
                  Cancel
                </button>
                <button className={styles.confirm} onClick={handleConfirmFlex}>
                  Confirm
                </button>
              </div>
            </div>
          </Property.Body>
        </Property.Dialog>
      }
    </>
  );
}

export const FlexConfigDialog = ({ id, widget, baseDefaults, target, targetRef, ...rest }) => {
  useFlexConfigDismissOnClickOutside(rest.onCancel, styles.flexConfigDialog);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const mobile = breakpoint === 'mobile';

  return (
    <Property.Dialog className={styles.flexConfigDialog}
      width={280}
      targetRef={targetRef}
      target={target || `#${id}`}
      placement="bottom"
      title={mobile ? 'Flex Layout' : 'Flex Layout'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <FlexConfigControls id={id} widget={widget} baseDefaults={baseDefaults} />
      </Property.Body>
    </Property.Dialog>
  );
}
